/**
 * Client-facing "Information & Documents" text blocks.
 *
 * Shape stored per trip (and in the agency template) since 2026-09-16:
 *     { blocks: [{ key, title, bullets: [string], hidden?: bool }] }
 *
 * Legacy shape (what the Travefy importer wrote and older trips still
 * carry) is an override map keyed by block id:
 *     { quote_terms: { title, bullets: [...] }, ... }
 * `resolveInfoBlocks` reads both so nothing has to be migrated.
 */

export const defaultInfoBlocks = (depositPct = 30) => [
  {
    key: "quote_terms",
    title: "Quote Terms",
    bullets: [
      "This quote is valid for 7 days; after this date, the price is subject to change based on availability.",
      "Based on the number of travellers and room occupancy indicated.",
      `A deposit of ${depositPct}% is required at the time of booking (full payment if travelling within 60 days).`,
      "Final payment is due 60 days before departure.",
      "Final travel documents are sent electronically 2 weeks before departure.",
      "No service or accommodation is reserved until the deposit is made.",
    ],
  },
  {
    key: "included",
    title: "What's Included",
    bullets: [
      "Accommodation as listed in the itinerary",
      "Activities and experiences as listed (unless marked optional)",
      "Transportation as listed in the itinerary",
      "24/7 in-trip traveller support",
    ],
  },
  {
    key: "not_included",
    title: "What's Not Included",
    bullets: [
      "International flights to/from the destination",
      "Meals not listed in the itinerary",
      "Optional activities",
      "Travel insurance (highly recommended)",
      "Gratuities and items of a personal nature",
      "Tourist city tax (paid directly at the hotel)",
    ],
  },
  {
    key: "cancellation",
    title: "Cancellation Policy",
    bullets: [
      "If you need to cancel, please notify us in writing. Cancellation is effective upon receipt of written notice. Cancellation terms vary by supplier; your travel advisor will make every effort to recover any applicable costs. By booking this trip you accept our terms and conditions.",
    ],
  },
];

/** True when the trip carries its own edited blocks (new shape). */
export const hasCustomBlocks = (info) => Array.isArray(info?.blocks) && info.blocks.length > 0;

/** True for the legacy override map the Travefy importer writes. */
export const hasLegacyOverrides = (info) =>
  !!info && Object.keys(info).some((k) => k !== "blocks" && Array.isArray(info[k]?.bullets));

/**
 * Legacy override map (Travefy import) merged over the code defaults.
 * Blocks Travefy never brings (typically Cancellation Policy) keep the
 * system copy; any override key we don't know about is appended.
 */
const legacyBlocks = (info, depositPct) => {
  const merged = defaultInfoBlocks(depositPct).map((b) => {
    const override = info?.[b.key];
    return override && Array.isArray(override.bullets) ? { ...b, ...override } : b;
  });
  const known = new Set(merged.map((b) => b.key));
  for (const [key, v] of Object.entries(info || {})) {
    if (key === "blocks" || known.has(key) || !Array.isArray(v?.bullets)) continue;
    merged.push({ key, title: v.title || key, bullets: v.bullets });
  }
  return merged;
};

/**
 * The blocks to render, in order. Priority:
 *   1. the trip's own edited blocks
 *   2. the text imported with the trip (legacy Travefy override map)
 *   3. the agency template
 *   4. the code defaults
 * The imported text MUST win over the template: adding an agency template
 * used to silently hide the copy that came from Travefy on trips that were
 * already published (bug reported 2026-09-18).
 */
export function resolveInfoBlocks(info, depositPct = 30, template = null) {
  if (hasCustomBlocks(info)) return info.blocks.filter((b) => !b.hidden);
  if (hasLegacyOverrides(info)) return legacyBlocks(info, depositPct).filter((b) => !b.hidden);
  if (hasCustomBlocks(template)) return template.blocks.filter((b) => !b.hidden);
  return defaultInfoBlocks(depositPct);
}

/** Same as above but keeps hidden blocks — the editor needs to show them. */
export function editableBlocks(info, depositPct = 30, template = null) {
  if (hasCustomBlocks(info)) return info.blocks;
  if (hasLegacyOverrides(info)) return legacyBlocks(info, depositPct);
  if (hasCustomBlocks(template)) return template.blocks;
  return defaultInfoBlocks(depositPct);
}

export const bulletsToText = (bullets) => (bullets || []).join("\n");

export const textToBullets = (text) =>
  (text || "")
    .split("\n")
    .map((l) => l.replace(/^\s*[•\-*]\s*/, "").trim())
    .filter(Boolean);
